import React, { useState, useEffect, useRef } from 'react';
import { Button, DatePicker } from 'antd';
import styles from './index.module.scss';
import dayjs, { Dayjs } from 'dayjs';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import SyncNow from 'components/Global/Sync';

// Extend dayjs with timezone plugins
dayjs.extend(utc);
dayjs.extend(timezone);

const TIMEZONE = process.env.REACT_APP_TIMEZONE || 'America/New_York';
const { RangePicker } = DatePicker;

type DateRangeOption =
	| 'today'
	| 'this-month'
	| 'previous-month'
	| 'last-3-months'
	| 'last-6-months'
	| 'custom';

interface SyncDateRangeDropdownProps {
	onSync: (fromDate: string, toDate: string) => void;
	isLoading?: boolean;
	lastSyncTime?: Date;
	defaultDateRange?: [Dayjs, Dayjs] | null;
}

const SyncDateRangeDropdown: React.FC<SyncDateRangeDropdownProps> = ({
	onSync,
	isLoading = false,
	lastSyncTime = new Date(),
	defaultDateRange = null,
}) => {
	const [selectedOption, setSelectedOption] =
		useState<DateRangeOption>('today');
	const [customRange, setCustomRange] = useState<[Dayjs, Dayjs] | null>(null);
	const [dropdownVisible, setDropdownVisible] = useState(false);
	const dropdownRef = useRef<HTMLDivElement>(null);

	// Helper function to get today's date range
	const getTodayRange = (): [Dayjs, Dayjs] => {
		const today = dayjs().tz(TIMEZONE);
		return [today.startOf('day'), today.endOf('day')];
	};

	// Helper function to get this month's date range
	const getThisMonthRange = (): [Dayjs, Dayjs] => {
		const today = dayjs().tz(TIMEZONE);
		const firstDayOfMonth = today.startOf('month');
		const lastDayOfMonth = today.endOf('month');
		return [firstDayOfMonth, lastDayOfMonth];
	};

	// Helper function to get previous month's date range
	const getPreviousMonthRange = (): [Dayjs, Dayjs] => {
		const today = dayjs().tz(TIMEZONE);
		const firstDayOfPreviousMonth = today.subtract(1, 'month').startOf('month');
		const lastDayOfPreviousMonth = today.subtract(1, 'month').endOf('month');
		return [firstDayOfPreviousMonth, lastDayOfPreviousMonth];
	};

	// Helper function to get last 3 months date range
	const getLast3MonthsRange = (): [Dayjs, Dayjs] => {
		const today = dayjs().tz(TIMEZONE);
		const threeMonthsAgo = today.subtract(3, 'month').startOf('month');
		const lastDayOfPreviousMonth = today.subtract(1, 'month').endOf('month');
		return [threeMonthsAgo, lastDayOfPreviousMonth];
	};

	// Helper function to get last 6 months date range
	const getLast6MonthsRange = (): [Dayjs, Dayjs] => {
		const today = dayjs().tz(TIMEZONE);
		const sixMonthsAgo = today.subtract(6, 'month').startOf('month');
		const lastDayOfPreviousMonth = today.subtract(1, 'month').endOf('month');
		return [sixMonthsAgo, lastDayOfPreviousMonth];
	};

	// Initialize with default date range or today's range
	useEffect(() => {
		if (defaultDateRange && defaultDateRange[0] && defaultDateRange[1]) {
			setCustomRange(defaultDateRange);
			setSelectedOption('custom');
		} else {
			setCustomRange(getTodayRange());
			setSelectedOption('today');
		}
	}, [defaultDateRange]);

	// Handle click outside to close dropdown
	useEffect(() => {
		const handleClickOutside = (event: MouseEvent) => {
			if (
				dropdownRef.current &&
				!dropdownRef.current.contains(event.target as Node)
			) {
				// Check if the click is on a date picker panel or related element
				const target = event.target as Element;
				const isDatePickerPanel =
					target.closest('.ant-picker-dropdown') ||
					target.closest('.ant-picker-panel') ||
					target.closest('.ant-picker') ||
					target.closest('.ant-picker-input');

				// Don't close if clicking on date picker elements
				if (!isDatePickerPanel) {
					setDropdownVisible(false);
				}
			}
		};

		if (dropdownVisible) {
			document.addEventListener('mousedown', handleClickOutside);
		}

		return () => {
			document.removeEventListener('mousedown', handleClickOutside);
		};
	}, [dropdownVisible]);

	const handleOptionSelect = (option: DateRangeOption) => {
		setSelectedOption(option);

		// Set default ranges based on selection
		switch (option) {
			case 'today':
				setCustomRange(getTodayRange());
				break;
			case 'this-month':
				setCustomRange(getThisMonthRange());
				break;
			case 'previous-month':
				setCustomRange(getPreviousMonthRange());
				break;
			case 'last-3-months':
				setCustomRange(getLast3MonthsRange());
				break;
			case 'last-6-months':
				setCustomRange(getLast6MonthsRange());
				break;
			case 'custom':
				break;
		}
	};

	const handleCustomRangeChange = (
		dates: [Dayjs | null, Dayjs | null] | null
	) => {
		if (dates && dates[0] && dates[1]) {
			setCustomRange([dates[0], dates[1]]);
		} else {
			setCustomRange(null);
		}
	};

	const handleSync = () => {
		if (!customRange || !customRange[0] || !customRange[1]) {
			return;
		}

		const fromDate = customRange[0].format('YYYY-MM-DD');
		const toDate = customRange[1].format('YYYY-MM-DD');

		onSync(fromDate, toDate);
		setDropdownVisible(false);
	};

	const handleCancel = () => {
		setDropdownVisible(false);
	};

	const getDateRangeDisplay = () => {
		if (!customRange) return '';

		const format = 'MM/DD/YYYY';
		return `${customRange[0].format(format)} - ${customRange[1].format(
			format
		)}`;
	};

	return (
		<div className={styles['sync-dropdown-container']} ref={dropdownRef}>
			<SyncNow
				syncDate={lastSyncTime}
				tooltip="Sync expense transactions from QuickBooks"
				handleSync={() => setDropdownVisible(!dropdownVisible)}
				isLastSyncNeeded={false}
				isLoading={isLoading}
				title="Sync Now"
			/>

			{dropdownVisible && (
				<div className={styles['dropdown-content']}>
					<div className={styles['date-preview']}>
						<strong>Selected Range:</strong> {getDateRangeDisplay()}
					</div>

					<div className={styles['options-section']}>
						<div className={styles['options-grid']}>
							<button
								className={`${styles['option-btn']} ${
									selectedOption === 'today' ? styles['selected'] : ''
								}`}
								onClick={() => handleOptionSelect('today')}
							>
								Today
							</button>
							<button
								className={`${styles['option-btn']} ${
									selectedOption === 'this-month' ? styles['selected'] : ''
								}`}
								onClick={() => handleOptionSelect('this-month')}
							>
								This Month
							</button>
							<button
								className={`${styles['option-btn']} ${
									selectedOption === 'previous-month' ? styles['selected'] : ''
								}`}
								onClick={() => handleOptionSelect('previous-month')}
							>
								Previous Month
							</button>
							<button
								className={`${styles['option-btn']} ${
									selectedOption === 'last-3-months' ? styles['selected'] : ''
								}`}
								onClick={() => handleOptionSelect('last-3-months')}
							>
								Last 3 Months
							</button>
							<button
								className={`${styles['option-btn']} ${
									selectedOption === 'last-6-months' ? styles['selected'] : ''
								}`}
								onClick={() => handleOptionSelect('last-6-months')}
							>
								Last 6 Months
							</button>
							<button
								className={`${styles['option-btn']} ${
									selectedOption === 'custom' ? styles['selected'] : ''
								}`}
								onClick={() => handleOptionSelect('custom')}
							>
								Custom Range
							</button>
						</div>
					</div>

					{selectedOption === 'custom' && (
						<div className={styles['custom-range-section']}>
							<label className={styles['range-label']}>
								Select Date Range:
							</label>
							<RangePicker
								value={customRange}
								onChange={handleCustomRangeChange}
								format="MM/DD/YYYY"
								placeholder={['Start Date', 'End Date']}
								className={styles['custom-range-picker']}
								size="large"
								allowClear={false}
							/>
						</div>
					)}

					<div className={styles['dropdown-actions']}>
						<Button
							size="small"
							onClick={handleCancel}
							className={styles['cancel-btn']}
						>
							Cancel
						</Button>
						<Button
							type="primary"
							size="small"
							onClick={handleSync}
							disabled={!customRange || !customRange[0] || !customRange[1]}
							className={styles['ok-btn']}
						>
							Sync
						</Button>
					</div>
				</div>
			)}
		</div>
	);
};

export default SyncDateRangeDropdown;
